import axios from 'axios';
import remarkGfm from 'remark-gfm';
import formatXml from 'xml-formatter';
import ReactMarkdown from 'react-markdown';
import { useNavigate } from 'react-router-dom';
import React, { useState, useEffect, useRef } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { dracula } from 'react-syntax-highlighter/dist/esm/styles/prism';

import { FaFileExcel, FaFileAlt, FaRocket, FaTrashAlt, FaCopy, FaRobot, FaSearch } from 'react-icons/fa';

const renderers = {
  code({ node, inline, className, children, ...props }: any) {
    const match = /language-(\w+)/.exec(className || '');
    return !inline && match ? (
      <SyntaxHighlighter style={dracula} language={match[1]} PreTag='div' {...props}>
        {String(children).replace(/\n$/, '')}
      </SyntaxHighlighter>
    ) : (
      <code className={className} {...props}>
        {children}
      </code>
    );
  }
};

const Chat: React.FC = () => {
  const [messages, setMessages] = useState<{ role: string; content: string }[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [idService, setIdService] = useState('');
  const [showServiceModal, setShowServiceModal] = useState<'submit' | 'delete' | null>(null);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [serviceId, setServiceId] = useState('');
  const [logs, setLogs] = useState<any[]>([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Проверка токена через 0.5 секунды после открытия страницы
  useEffect(() => {
    const checkToken = () => {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
      }
    };
    const timer = setTimeout(checkToken, 100);
    return () => clearTimeout(timer);
  }, [navigate]);

  // Прокрутка вниз при добавлении нового сообщения
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim()) return;
    const newMessages = [...messages, { role: 'user', content: input }];
    setMessages(newMessages);
    setInput('');
    setLoading(true);
    try {
      const response = await axios.post(
        '/chat',
        { messages: newMessages },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );
      const { role, content } = response.data.choices[0].message;
      setMessages(prev => [...prev, { role, content }]);
    } catch (error) {
      console.error('Ошибка при отправке:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const copyChatContent = () => {
    const chatText = messages.map(msg => `${msg.role}: ${msg.content}`).join('\n');
    navigator.clipboard.writeText(chatText);
    alert('Чат скопирован!');
  };

  const downloadFile = async (url: string, filename: string) => {
    try {
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        responseType: 'blob'
      });
      const urlBlob = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = urlBlob;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      alert('Ошибка при скачивании файла');
    }
  };

  const submitService = async () => {
    if (!idService) return;
    try {
      await axios.post(
        '/services',
        { idService: parseInt(idService) },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );
      alert('Сервис подан');
      setShowServiceModal(null);
    } catch (error) {
      alert('Ошибка при подаче сервиса');
    }
  };

  const deleteService = async () => {
    if (!idService) return;
    try {
      await axios.delete('/services', {
        data: { idService: parseInt(idService) },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      alert('Сервис удалён');
      setShowServiceModal(null);
    } catch (error) {
      alert('Ошибка при удалении сервиса');
    }
  };

  const fetchLogs = async () => {
    if (!serviceId) return;
    setLoadingLogs(true);
    try {
      const response = await axios.get(`/graylog?idService=${serviceId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setLogs(response.data);
    } catch (error) {
      alert('Ошибка при получении логов');
    } finally {
      setLoadingLogs(false);
    }
  };

  return (
    <div className='flex flex-col h-screen bg-gray-100'>
      {/* Заголовок */}
      <header className='bg-white shadow-md p-4 flex justify-between items-center'>
        <h1 className='text-xl font-semibold'>🤖 Чат с LLM-K24</h1>
        <button onClick={handleLogout} className='text-sm text-gray-500 hover:text-gray-700'>
          Выйти
        </button>
      </header>

      {/* Область сообщений */}
      <div className='flex-1 overflow-y-auto p-4 pb-40'>
        {messages.map((msg, index) => (
          <div
            key={index}
            className={`p-3 max-w-lg rounded-lg mb-2 ${
              msg.role === 'user' ? 'bg-blue-500 text-white ml-auto' : 'bg-gray-200 text-black'
            }`}
          >
            <ReactMarkdown components={renderers} remarkPlugins={[remarkGfm]}>
              {msg.content}
            </ReactMarkdown>
          </div>
        ))}
        {loading && <div className='text-gray-500'>AI печатает...</div>}
        <div ref={chatEndRef} />
      </div>

      {/* Фиксированная область ввода и иконок */}
      <div className='fixed bottom-0 left-0 right-0 bg-white p-4 shadow-md'>
        <div className='flex items-center mb-2'>
          <textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyPress={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
            className='flex-1 p-2 border rounded resize-none focus:outline-none'
            placeholder='Введите сообщение...'
            rows={2}
          />
        </div>
        <div className='flex justify-around'>
          <button
            onClick={() => downloadFile('/services', 'services.xlsx')}
            title='Скачать файл сервисов (Excel)'
            className='text-2xl'
          >
            <FaFileExcel color='green' />
          </button>
          <button onClick={() => downloadFile('/docs', 'docs.md')} title='Скачать документацию (MD)' className='text-2xl'>
            <FaFileAlt color='gray' />
          </button>
          <button onClick={() => setShowServiceModal('submit')} title='Подать сервис на имитатор' className='text-2xl'>
            <FaRocket color='orange' />
          </button>
          <button onClick={() => setShowServiceModal('delete')} title='Удалить сервис с имитатора' className='text-2xl'>
            <FaTrashAlt color='red' />
          </button>
          <button onClick={copyChatContent} title='Копировать содержимое чата' className='text-2xl'>
            <FaCopy color='lightgray' />
          </button>
          <button onClick={() => setShowLogsModal(true)} title='Получить логи' className='text-2xl'>
            <FaSearch color='blue' />
          </button>
          <button
            onClick={sendMessage}
            disabled={loading}
            title='Отправить сообщение'
            className={`text-2xl ${loading ? 'opacity-50' : ''}`}
          >
            <FaRobot color='brown' />
          </button>
        </div>
      </div>

      {/* Модальное окно для сервисов */}
      {showServiceModal && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center'>
          <div className='bg-white p-6 rounded-lg shadow-md'>
            <h3 className='text-xl mb-4'>{showServiceModal === 'submit' ? 'Подать сервис' : 'Удалить сервис'}</h3>
            <input
              type='text'
              value={idService}
              onChange={e => setIdService(e.target.value)}
              placeholder='ID сервиса'
              className='w-full p-2 mb-4 border rounded'
            />
            <div className='flex justify-end'>
              <button onClick={() => setShowServiceModal(null)} className='mr-2 px-4 py-2 bg-gray-300 rounded'>
                Отмена
              </button>
              <button
                onClick={showServiceModal === 'submit' ? submitService : deleteService}
                className='px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600'
              >
                {showServiceModal === 'submit' ? 'Подать' : 'Удалить'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно для логов */}
      {showLogsModal && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center'>
          <div className='bg-white p-6 rounded-lg shadow-md w-3/4 max-w-4xl'>
            <h3 className='text-xl mb-4'>Получить логи</h3>
            <div className='flex mb-4'>
              <input
                type='text'
                value={serviceId}
                onChange={e => setServiceId(e.target.value)}
                placeholder='ID сервиса'
                className='flex-1 p-2 border rounded mr-2'
              />
              <button
                onClick={fetchLogs}
                disabled={loadingLogs}
                className={`px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 ${loadingLogs ? 'opacity-50' : ''}`}
              >
                {loadingLogs ? 'Поиск...' : 'Поиск'}
              </button>
            </div>
            {logs.length > 0 && (
              <div className='overflow-y-auto' style={{ maxHeight: '500px' }}>
                {logs.map((log, index) => (
                  <div key={index} className='mb-4'>
                    <p>
                      <strong>Время запроса:</strong> {log.message.timestamp}
                    </p>
                    <p>
                      <strong>Отправленное сообщение:</strong>
                    </p>
                    <SyntaxHighlighter language='xml' style={dracula}>
                      {formatXml(log.message.receiveMessage)}
                    </SyntaxHighlighter>
                    <p>
                      <strong>Полученное сообщение:</strong>
                    </p>
                    <SyntaxHighlighter language='xml' style={dracula}>
                      {formatXml(log.message.sendMessage)}
                    </SyntaxHighlighter>
                  </div>
                ))}
              </div>
            )}
            <div className='flex justify-end mt-4'>
              <button onClick={() => setShowLogsModal(false)} className='px-4 py-2 bg-gray-300 rounded'>
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Chat;
